import React, { FC, forwardRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar, SnackbarKey } from 'notistack';
import { useNavigate } from 'react-router-dom';
// Core & Lab
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
// Types
import { AppState } from '../../reducers';
import { setTokenExpiresAt } from '../profile/redux/actions';
import { openReauthLogin } from '../../utils/reauthLogin';

const WARNING_BEFORE_MS = 20 * 60 * 1000; // 20 minutes avant expiration

type TokenExpirySnackbarContentProps = {
    id: SnackbarKey;
    onReconnect: () => void;
};

const TokenExpirySnackbarContent = forwardRef<HTMLDivElement, TokenExpirySnackbarContentProps>(
    ({ id, onReconnect }, ref) => {
        const { t } = useTranslation();
        const { closeSnackbar } = useSnackbar();

        const handleReconnect = () => {
            closeSnackbar(id);
            onReconnect();
        };

        const handleContinue = () => {
            closeSnackbar(id);
        };

        return (
            <div ref={ref}>
                <Alert
                    severity="warning"
                    sx={{ minWidth: 420 }}
                    action={
                        <Box display="flex" gap={1} alignItems="center" flexShrink={0}>
                            <Button
                                color="inherit"
                                size="small"
                                variant="outlined"
                                onClick={handleReconnect}
                            >
                                {t('shared.reconnect-now')}
                            </Button>
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleContinue}
                            >
                                {t('shared.continue')}
                            </Button>
                        </Box>
                    }
                >
                    {t('shared.token-expiry-warning')}
                </Alert>
            </div>
        );
    }
);

TokenExpirySnackbarContent.displayName = 'TokenExpirySnackbarContent';

const TokenExpiryWarning: FC = () => {
    const token_expires_at = useSelector((state: AppState) => state.profile.token_expires_at);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const snackbarKeyRef = useRef<SnackbarKey | null>(null);

    const forceLogout = () => {
        if (snackbarKeyRef.current) {
            closeSnackbar(snackbarKeyRef.current);
            snackbarKeyRef.current = null;
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        navigate('/login');
    };

    const showWarning = () => {
        if (snackbarKeyRef.current) {
            closeSnackbar(snackbarKeyRef.current);
        }
        snackbarKeyRef.current = enqueueSnackbar('', {
            variant: 'warning',
            persist: true,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            // Nouvel onglet : rediriger CET onglet vers la connexion faisait perdre la page en cours.
            content: (key) => <TokenExpirySnackbarContent id={key} onReconnect={openReauthLogin} />,
        });
    };

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        if (snackbarKeyRef.current) {
            closeSnackbar(snackbarKeyRef.current);
            snackbarKeyRef.current = null;
        }

        if (!token_expires_at) {
            return;
        }

        const expiresMs = new Date(token_expires_at).getTime();
        if (Number.isNaN(expiresMs)) {
            return;
        }

        const now = Date.now();
        const warningDelay = expiresMs - WARNING_BEFORE_MS - now;
        const logoutDelay = expiresMs - now;

        // Token déjà expiré : redirect immédiat, pas de snackbar
        if (logoutDelay <= 0) {
            forceLogout();
            return;
        }

        // Token expirant bientôt : afficher l'avertissement
        if (warningDelay <= 0) {
            showWarning();
        } else {
            timerRef.current = setTimeout(showWarning, warningDelay);
        }

        logoutTimerRef.current = setTimeout(forceLogout, logoutDelay);

        // Vérification à la reprise de l'onglet (onglet mis en veille / navigateur suspendu)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && Date.now() >= expiresMs) {
                forceLogout();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [token_expires_at]);

    // Reconnexion faite dans un autre onglet : la nouvelle échéance remplace celle-ci, sans quoi la
    // déconnexion programmée tombait quand même à l'ancienne heure.
    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key === 'token_expires_at' && event.newValue) dispatch(setTokenExpiresAt(event.newValue));
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    return null;
};

export default TokenExpiryWarning;
